import React, { useRef, useState, useEffect } from "react";

import { Form } from "antd";
import { getMessageStep } from "../../services/api/Api";
import { useDispatch } from "react-redux";
import { setComments } from "../../appRedux/actions/FormsV2";
import { useIntl } from "react-intl";
import IntlMessages from "@util/IntlMessages";
import { useDebouncedCallback } from "use-debounce";
import { getFile, sendFile, addComment } from "../../services/restAPI/RestApi";
import { Message, Button } from "../BP2Components";
import { useWindowSize } from "@util/customHooks";

import "./addComments.less";
import { Icon_SendComment } from "../BP2Components/Icons/icons";
import MessageButtonsDropdown from "../FormsV2/MessageButtons/MessageButtonsDropdown/MessageButtonsDropdown";
import Spinner from "../BP2Components/Spinner";

// Because Safari does not support Regex negative lookbehind, we need to specify list of
// strings that cannot be inside a link for it to be replaced with a tag
const matchFailers = [".jpg", ".webp", ".jpeg", ".png", "googleusercontent"];

const AddComments = ({
   modalMode = false,
   setCommentValue,
   form,
   instanceId,
   setContentLoading,
   recordId,
   actionId,
   customSetComments,
   className = "",
   focusTextArea,
   onSendComment,
   buttons,
   currentProcessStep,
   isQuestionMode,
   sysBtnsConfig,
   setCurrentProcessStep,
   actionsVisible = false,
}) => {
   const dispatch = useDispatch();
   const intl = useIntl();
   const ref = useRef(null);
   const { getFieldDecorator, setFieldsValue, getFieldsValue } = form;
   const [imgLoading, setImgLoading] = useState(false);
   const [isImageOpen, setIsImageOpen] = useState(false);
   const [isCommented, setIsCommented] = useState(false);
   const [isSendingComment, setIsSendingComment] = useState(false);
   const [imageSrc, setImageSrc] = useState("");
   const [handledLinks, setHandledLinks] = useState([]);
   const { width: windowWidth } = useWindowSize();

   useEffect(() => {
      setContentLoading && setContentLoading(imgLoading);
   }, [imgLoading]);

   const handlePaste = async (event) => {
      const items = event.clipboardData.items;

      const urls = [];

      for (var i = 0; i < items.length; i++) {
         // Skip content if not image
         if (items[i]?.type.indexOf("image") == -1) continue;

         try {
            setImgLoading(true);
            Message.loading(intl.formatMessage({ id: "global.loading" }), 0);
            // Retrieve image on clipboard as blob
            const file = items[i].getAsFile();

            const formData = new FormData();

            formData.append("multipartFile", file);

            const fileId = (await sendFile(formData)).data;
            const fileUrl = (await getFile(fileId)).data;
            urls.push(fileUrl);

            Message.destroy();
            Message.success(intl.formatMessage({ id: "global.success" }));
            setImgLoading(false);
         } catch {
            Message.destroy();
            Message.error(intl.formatMessage({ id: "global.error" }));
         }
      }

      const imgAlt = "bp2 google image";

      setTimeout(() => {
         const imgTags = ref.current.querySelectorAll(`img:not([alt='${imgAlt}'])`);
         const imgsToChange = Array.from(imgTags).filter((tag) =>
            tag.currentSrc.includes("data:image"),
         );

         imgsToChange.forEach((img, i) => {
            setHandledLinks((prevLinks) => [...prevLinks, urls[i]]);
            img.src = urls[i].replace(/=s220$/, "");
            img.referrerPolicy = "no-referrer";
            img.alt = imgAlt;
         });

         handleTextareaChange(ref.current.innerHTML);
      }, 0);
   };

   const handleLinks = (value) => {
      try {
         const urlRegex =
            /(?!(^src|^href)=["'])https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/g;

         const changed = value.replaceAll(urlRegex, (match) => {
            if (handledLinks.includes(match)) return match;
            setHandledLinks((prevLinks) => [...prevLinks, match]);
            return `<a href=${match} target='_blank' style='cursor: pointer;' contentEditable='false'>${match}</a>`;
         });
         if (!ref.current || value === changed) return value;

         // Save previous cursor index
         const selection = window.getSelection();

         const rangeCopy = selection?.getRangeAt(0).cloneRange();

         const prevSelectionDetails = {
            endContainer: rangeCopy?.endContainer,
            endOffset: rangeCopy?.endOffset,
         };

         // Update HTML with new anchor tags
         ref.current.innerHTML = changed;

         const newRange = new Range();

         const textNode = document
            .evaluate(
               '//*[text()="' + prevSelectionDetails.endContainer?.nodeValue + '"]',
               document,
               null,
               XPathResult.ORDERED_NODE_SNAPSHOT_TYPE,
            )
            .snapshotItem(0);

         // Set cursor index
         selection.removeAllRanges();
         newRange.collapse(true);
         newRange.setStartAfter(textNode);
         selection.addRange(newRange);
         return changed;
      } catch {
         return value;
      }
   };

   // Contenteditable leaves "<br>" or "&nbsp;" when cleared — normalize to "" so
   // required-field validation sees an empty value instead of a non-empty HTML fragment.
   const normalizeHtml = (html) => {
      if (html === "") return "";
      const text = html
         .replace(/&nbsp;|&#160;/g, " ")
         .replace(/\u00A0/g, " ")
         .replace(/<[^>]*>/g, "")
         .trim();
      return text === "" ? "" : html;
   };

   const handleTextareaChange = useDebouncedCallback((value) => {
      const normalizedValue = normalizeHtml(handleLinks(value));
      setFieldsValue({ comment: normalizedValue });
      setCommentValue && setCommentValue(normalizedValue);
   }, 1000);

   const sendComment = () => {
      // flush handleTextareaChange in order to update the form field immediately
      handleTextareaChange.flush();

      if (!getFieldsValue()?.comment) return;

      const query = new URLSearchParams(window.location.search);
      const actionIdFromUrl = query.get("actionId");
      if (actionIdFromUrl || actionId) {
         setIsSendingComment(true);
         addComment(
            { msgId: actionIdFromUrl ?? actionId },
            getFieldsValue()?.comment?.replaceAll(/\"/gm, '\\"'),
         )
            .then(() => {
               setHandledLinks([]);
               return getMessageStep(actionIdFromUrl ?? actionId);
            })
            .then((message) => {
               customSetComments
                  ? customSetComments(message?.data?.step?.comments)
                  : dispatch(setComments(message?.data?.step?.comments));
               ref.current.innerHTML = "";
               setFieldsValue({ comment: "" });
            })
            .catch((err) => {
               console.error("Failed to send or refresh comments", err);
               Message.error(intl.formatMessage({ id: "global.error" }));
            })
            .finally(() => {
               setIsSendingComment(false);
            });
      }
   };

   useEffect(() => {
      onSendComment && sendComment();
   }, [onSendComment]);

   const handleMouseDown = (event) => {
      if (event.target.tagName === "IMG") {
         setIsImageOpen(true);
         setImageSrc(event.target.src);
      }
   };

   const handleInput = (e) => {
      if (e.nativeEvent.inputType === "insertFromPaste") {
         return;
      }

      if (!handledLinks.every((link) => e.target.innerHTML.includes(link))) {
         setHandledLinks((prevLinks) =>
            prevLinks.filter((link) => e.target.innerHTML.includes(link)),
         );
      }

      const normalized = normalizeHtml(e.target.innerHTML);
      handleTextareaChange(e.target.innerHTML);
      setCommentValue && setCommentValue(normalized);
      setIsCommented(normalized !== "");
   };

   const closeImage = () => {
      setIsImageOpen(false);
   };

   useEffect(() => {
      focusTextArea && ref.current.focus();
      const handleKeyDown = (event) => {
         if (event.key === "Escape") {
            closeImage();
         }
      };

      window.addEventListener("keydown", handleKeyDown);

      return () => {
         window.removeEventListener("keydown", handleKeyDown);
      };
   }, []);
   return (
      <div
         className={`${className} add-comments-container ${
            modalMode ? "modal-mode" : "comments-mode"
         }`}
      >
         {getFieldDecorator("comment")(
            <div
               contentEditable={true}
               ref={ref}
               onPaste={(e) => handlePaste(e)}
               onInput={(e) => handleInput(e)}
               onMouseDown={handleMouseDown}
               className={`comment-textarea ${modalMode ? "modal-mode" : "comments-mode"}`}
               data-placeholder={
                  !modalMode ? intl.formatMessage({ id: "messages.writeComment" }) : ""
               }
            />,
         )}
         {!modalMode && (
            <div className="add-comments-container__buttons">
               {actionsVisible && (
                  <MessageButtonsDropdown
                     setCurrentProcessStep={setCurrentProcessStep}
                     systemButtons={buttons.systemButtons}
                     normalButtons={buttons.normalButtons}
                     windowWidth={windowWidth}
                     currentProcessStep={currentProcessStep}
                     sysBtnsConfig={sysBtnsConfig}
                     isQuestionMode={isQuestionMode}
                     btnClassName={`${
                        isCommented
                           ? "add-comments-container__buttons__dropdown-btn add-comments-container__buttons__dropdown-btn--commented"
                           : "add-comments-container__buttons__dropdown-btn"
                     }`}
                  />
               )}
               <Button
                  key="btn"
                  buttonType={imgLoading ? "disabled" : "third"}
                  htmlType="submit"
                  onClick={() => sendComment()}
                  disabled={isSendingComment}
                  style={
                     isCommented
                        ? {
                             backgroundColor: "var(--btn-primaryBackground)",
                             color: "var(--btn-primaryText)",
                             borderColor: "var(--btn-primaryBackground)",
                          }
                        : {}
                  }
               >
                  {isSendingComment ? (
                     <Spinner />
                  ) : !customSetComments ? (
                     <IntlMessages id="messages.addComment" />
                  ) : (
                     <Icon_SendComment fill="#64748B" stroke="none" />
                  )}
               </Button>
            </div>
         )}
         {isImageOpen && (
            <div className="fullscreen-image-overlay" onClick={closeImage}>
               <img src={imageSrc} alt="Image" />
            </div>
         )}
      </div>
   );
};

export default Form.create()(AddComments);
